import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {Formik} from 'formik';
import moment from 'moment/moment';
import {ConnectedFocusError} from 'focus-formik-error';
import {
  RDBlock,
  RDBlockButton,
  RDBlockLeft,
  RDBlockRight,
  RDTitleBlock,
  RDTitleLabel
} from 'routes/App/discountsV2/RecommendedDiscounts/styles';
import DiscountsStatsBanner from 'routes/App/discountsV2/RecommendedDiscounts/DiscountsStatsBanner';
import DiscountCategoryCarousel from 'routes/App/discountsV2/RecommendedDiscounts/DiscountCategoryCarousel';
import {
  DISCOUNT_POSTHOG_EVENTS,
  DISCOUNT_TYPE,
  RECOMMENDED_DISCOUNT_CATEGORIES,
  RECOMMENDED_DISCOUNT_CONFIG,
  RECOMMENDED_DISCOUNTS
} from 'helpers/constants/discountsV2';
import {
  CLEAR_DISCOUNT,
  CREATE_DISCOUNT,
  SET_BUTTON_STATUS,
  SET_DISCOUNT,
  SET_DISCOUNT_MODAL,
  UPDATE_DISCOUNT
} from 'store/discountsV2/action';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  createBaseDiscount,
  getDiscountTypeForPostHog
} from 'helpers/functionUtils/discountsV2';
import DiscountV2Schema from 'helpers/services/formValidationSchemas/DiscountV2Schema';
import {DaysPickerContextProvider} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import BogoSettingsModal from 'routes/App/discountsV2/DiscountWizard/BogoSettingsModal';
import ItemSettingsModal from 'routes/App/discountsV2/DiscountWizard/ItemSettingsModal';
import SettingsModal from 'routes/App/discountsV2/DiscountWizard/SettingsModal';
import {
  getButtonStatus,
  getDiscount,
  getIsOpenModal
} from 'store/discountsV2/selectors';

const RecommendedDiscounts = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const formikRef = useRef();
  const {
    AllowedServiceFeatures,
    Timezone,
    _id,
    BusinessName,
    FullName
  } = useSelector((state) => state.user.activeLocation);
  const {userData} = useSelector((state) => state.user);
  const discount = getDiscount();
  const buttonStatus = getButtonStatus();
  const isOpenModal = getIsOpenModal();

  const cards = useMemo(() => {
    // Use RECOMMENDED_DISCOUNTS directly in the order defined in the constants file
    return RECOMMENDED_DISCOUNTS;
  }, []);

  const [enabledDiscount, setEnabledDiscount] = useState(false);
  const [enabledDate, setEnabledDate] = useState(false);
  const [enabledCustomAvailability, setEnabledCustomAvailability] = useState(
    false
  );
  const [enabledAdvancedSettings, setEnabledAdvancedSettings] = useState(false);

  useEffect(() => {
    if (discount?.name) {
      setEnabledCustomAvailability(
        discount?.availability?.activatesAtLocal ||
          discount?.availability?.deactivatesAtLocal ||
          discount?.availability?.weekly !== null
      );
      setEnabledDate(
        discount?.availability?.activatesAtLocal ||
          discount?.availability?.deactivatesAtLocal
      );
      setEnabledDiscount(
        discount.promoCode !== null && discount.promoCode?.code
      );
      setEnabledAdvancedSettings(discount?.availability?.weekly !== null);
    }
  }, [discount]);

  const selectDiscount = (id) => {
    const base = createBaseDiscount();
    const config = RECOMMENDED_DISCOUNT_CONFIG[id];

    const newDiscount = {
      ...base,
      ...config,
      rate: {
        ...base.rate,
        ...(config.rate || {})
      },
      subtotal: {
        ...base.subtotal,
        ...(config.subtotal || {})
      },
      fulfillment: {
        ...base.fulfillment,
        ...(config.fulfillment || {})
      },
      customers: {
        ...base.customers,
        ...(config.customers || {})
      },
      availability: {
        ...base.availability,
        ...(config.availability || {})
      },
      promoCode: {
        ...base.promoCode,
        ...(config.promoCode || {})
      },
      marketing: {
        ...base.marketing,
        ...(config.marketing || {}),
        email: {
          ...base.marketing.email,
          ...(config.marketing && config.marketing.email
            ? config.marketing.email
            : {})
        }
      }
    };

    dispatch(SET_DISCOUNT(newDiscount));
    dispatch(SET_DISCOUNT_MODAL(true));
  };

  const onCloseAction = () => {
    dispatch(CLEAR_DISCOUNT());
    formikRef?.current?.resetForm();
    dispatch(SET_DISCOUNT_MODAL(false));
    setEnabledCustomAvailability(false);
    setEnabledAdvancedSettings(false);
    setEnabledDate(false);
    setEnabledDiscount(false);
  };

  const renderCard = (card) => (
    <RDBlock key={card.id}>
      <RDBlockLeft className='textOverlap'>
        <div>
          <RDTitleBlock>
            <div className='bold'>{card.title}</div>
            {card.label && (
              <RDTitleLabel className={card.label}>
                {card.label.toUpperCase()}
              </RDTitleLabel>
            )}
          </RDTitleBlock>

          <div className='description'>{card.description}</div>
          {card.advice && <div className='advice'>{card.advice}</div>}
          <div className='visual'>{card.visual}</div>
        </div>

        <RDBlockButton
          onClick={() => {
            selectDiscount(card.id);
          }}
        >
          <span>Select</span>
        </RDBlockButton>
      </RDBlockLeft>

      <RDBlockRight className='allowOverlap'>
        <img src={card.img} alt={card.id} />
      </RDBlockRight>
    </RDBlock>
  );

  return (
    <>
      <DiscountsStatsBanner />
      {RECOMMENDED_DISCOUNT_CATEGORIES.map((category) => {
        const categoryCards = cards.filter(
          (card) => card.category === category.key
        );
        if (!categoryCards.length) return null;
        return (
          <DiscountCategoryCarousel key={category.key} category={category}>
            {categoryCards.map(renderCard)}
          </DiscountCategoryCarousel>
        );
      })}
      <Formik
        initialValues={{
          ...discount
        }}
        innerRef={formikRef}
        validationSchema={DiscountV2Schema}
        enableReinitialize
        onSubmit={async (
          values,
          {setFieldError, setSubmitting, validateForm}
        ) => {
          const payload = {...values};
          const schemaErrors = await validateForm();
          if (schemaErrors && Object.keys(schemaErrors).length > 0) {
            setSubmitting(false);
            if (window?.posthog) {
              window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                result: 'failed',
                mode: discount.id ? 'edit' : 'new',
                discount_type: getDiscountTypeForPostHog(payload),
                location_id: _id,
                location_name: BusinessName || FullName,
                user_id: userData?._id,
                user_email: userData?.email,
                timestamp: moment().utc().format()
              });
            }
            return;
          }

          if (enabledDiscount && !payload?.promoCode?.code) {
            setFieldError(
              'promoCode.code',
              'Promo Code field cannot be empty!'
            );
            setSubmitting(false);
            if (window?.posthog) {
              window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                result: 'failed',
                mode: 'new',
                discount_type: getDiscountTypeForPostHog(payload),
                location_id: _id,
                location_name: BusinessName || FullName,
                user_id: userData?._id,
                user_email: userData?.email,
                timestamp: moment().utc().format()
              });
            }
            return;
          }

          if (
            (enabledDate &&
              (!payload?.availability?.activatesAtLocal ||
                payload?.availability?.activatesAtLocal === 'Invalid date')) ||
            (enabledDate &&
              (!payload?.availability?.deactivatesAtLocal ||
                payload?.availability?.deactivatesAtLocal === 'Invalid date'))
          ) {
            setFieldError(
              'availability.activatesAtLocal',
              'Please select start and end date!'
            );
            setSubmitting(false);
            if (window?.posthog) {
              window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                result: 'failed',
                mode: 'new',
                discount_type: getDiscountTypeForPostHog(payload),
                location_id: _id,
                location_name: BusinessName || FullName,
                user_id: userData?._id,
                user_email: userData?.email,
                timestamp: moment().utc().format()
              });
            }
            return;
          }

          if (
            (payload?.rate?.type === DISCOUNT_TYPE.BOGO ||
              payload?.type === DISCOUNT_TYPE.BOGO) &&
            (!payload?.bogo?.discounted || !payload?.bogo?.discounted?.length)
          ) {
            setFieldError(
              'bogo.discounted',
              'Please select items for BOGO discount!'
            );
            setSubmitting(false);
            if (window?.posthog) {
              window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                result: 'failed',
                mode: 'new',
                discount_type: getDiscountTypeForPostHog(payload),
                location_id: _id,
                location_name: BusinessName || FullName,
                user_id: userData?._id,
                user_email: userData?.email,
                timestamp: moment().utc().format()
              });
            }
            return;
          }

          if (
            (payload?.rate?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS ||
              payload?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS) &&
            (!payload?.specificItems?.discounted ||
              !payload?.specificItems?.discounted?.length)
          ) {
            setFieldError(
              'specificItems.discounted',
              'Please select items for discount!'
            );
            setSubmitting(false);
            if (window?.posthog) {
              window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                result: 'failed',
                mode: 'new',
                discount_type: getDiscountTypeForPostHog(payload),
                location_id: _id,
                location_name: BusinessName || FullName,
                user_id: userData?._id,
                user_email: userData?.email,
                timestamp: moment().utc().format()
              });
            }
            return;
          }

          if (payload?.promoCode?.code) {
            payload.promoCode.code = payload?.promoCode?.code?.toUpperCase();
          }

          if (
            payload?.rate?.type === DISCOUNT_TYPE.PERCENTAGE &&
            !payload?.subtotal?.min
          ) {
            payload.subtotal.min = 0;
          }
          if (
            payload?.rate?.type === DISCOUNT_TYPE.BOGO ||
            payload?.type === DISCOUNT_TYPE.BOGO
          ) {
            payload.type = DISCOUNT_TYPE.BOGO;
            payload.rate.type = DISCOUNT_TYPE.PERCENTAGE;
            payload.rate.value = 100;
          }

          if (!payload?.creationReason) {
            payload.creationReason = 'IncreaseOrderVolume';
          }

          if (payload?.promoCode !== null && payload?.promoCode?.usageLimit) {
            payload.promoCode.usageLimit.activatesAtLocal = payload?.promoCode
              ?.usageLimit?.activatesAtLocal
              ? payload.promoCode?.usageLimit?.activatesAtLocal
              : moment()
                  .utcOffset(Number(Timezone) || 0, false)
                  .utc(true)
                  .format();
          }
          if (!payload?.creationReason) {
            payload.creationReason = 'IncreaseOrderVolume';
          }

          dispatch(SET_BUTTON_STATUS(true));

          dispatch(
            CREATE_DISCOUNT({
              payload,
              postCallback: () => {
                onCloseAction();
                if (window?.posthog) {
                  window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                    result: 'success',
                    mode: 'new',
                    discount_type: getDiscountTypeForPostHog(payload),
                    location_id: _id,
                    location_name: BusinessName || FullName,
                    user_id: userData?._id,
                    user_email: userData?.email,
                    timestamp: moment().utc().format()
                  });
                }
              },
              locationPath: location.pathname,
              errorCallback: () => {
                if (window?.posthog) {
                  window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                    result: 'failed',
                    mode: 'new',
                    discount_type: getDiscountTypeForPostHog(payload),
                    location_id: _id,
                    location_name: BusinessName || FullName,
                    user_id: userData?._id,
                    user_email: userData?.email,
                    timestamp: moment().utc().format()
                  });
                }
              }
            })
          );
        }}
      >
        {(formik) => {
          useEffect(() => {
            if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(formik.values),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
            }
          }, [formik.submitCount, formik.isSubmitting, formik.errors]);
          return (
            <>
              <ConnectedFocusError />
              <DaysPickerContextProvider>
                {isOpenModal &&
                  (formik?.values?.type === DISCOUNT_TYPE.BOGO ||
                    formik?.values?.bogo?.discounted?.length) && (
                    <BogoSettingsModal
                      discount={discount}
                      formik={formik}
                      enabledDiscount={enabledDiscount}
                      setEnabledDiscount={setEnabledDiscount}
                      enabledDate={enabledDate}
                      setEnabledDate={setEnabledDate}
                      buttonStatus={buttonStatus}
                      allowedServiceFeatures={AllowedServiceFeatures}
                      enabledCustomAvailability={enabledCustomAvailability}
                      setEnabledCustomAvailability={
                        setEnabledCustomAvailability
                      }
                      enabledAdvancedSettings={enabledAdvancedSettings}
                      setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                      isOpen={isOpenModal}
                      onCloseAction={onCloseAction}
                    />
                  )}
                {isOpenModal &&
                  (formik?.values?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS ||
                    formik?.values?.specificItems?.discounted?.length) && (
                    <ItemSettingsModal
                      discount={discount}
                      formik={formik}
                      enabledDiscount={enabledDiscount}
                      setEnabledDiscount={setEnabledDiscount}
                      enabledDate={enabledDate}
                      setEnabledDate={setEnabledDate}
                      buttonStatus={buttonStatus}
                      allowedServiceFeatures={AllowedServiceFeatures}
                      enabledCustomAvailability={enabledCustomAvailability}
                      setEnabledCustomAvailability={
                        setEnabledCustomAvailability
                      }
                      enabledAdvancedSettings={enabledAdvancedSettings}
                      setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                      isOpen={isOpenModal}
                      onCloseAction={onCloseAction}
                    />
                  )}
                {isOpenModal &&
                  formik?.values?.type === DISCOUNT_TYPE.WHOLE_CART && (
                    <SettingsModal
                      discount={discount}
                      formik={formik}
                      enabledDiscount={enabledDiscount}
                      setEnabledDiscount={setEnabledDiscount}
                      enabledDate={enabledDate}
                      setEnabledDate={setEnabledDate}
                      buttonStatus={buttonStatus}
                      allowedServiceFeatures={AllowedServiceFeatures}
                      enabledCustomAvailability={enabledCustomAvailability}
                      setEnabledCustomAvailability={
                        setEnabledCustomAvailability
                      }
                      enabledAdvancedSettings={enabledAdvancedSettings}
                      setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                      isOpen={isOpenModal}
                      onCloseAction={onCloseAction}
                    />
                  )}
              </DaysPickerContextProvider>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default RecommendedDiscounts;
